import { 
  Relationship, 
  CreateRelationshipData, 
  UpdateRelationshipData, 
  RelationshipFilters, 
  RelationshipResponse,
  RelationshipStats,
  ObjectType 
} from '../types/Relationship';
import { makeAuthenticatedRequest } from './authService';
import { logApiConfig } from '../config/api';

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  logApiConfig();
}

class RelationshipService {
  // Create a new relationship
  async createRelationship(relationshipData: CreateRelationshipData): Promise<RelationshipResponse> {
    try {
      const response = await makeAuthenticatedRequest('/relationships', {
        method: 'POST',
        body: JSON.stringify(relationshipData)
      });
      
      return response;
    } catch (error) {
      console.error('Error creating relationship:', error);
      throw error;
    }
  }

  // Get relationship by ID
  async getRelationshipById(relationshipId: number): Promise<RelationshipResponse> {
    try {
      const response = await makeAuthenticatedRequest(`/relationships/${relationshipId}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching relationship:', error);
      throw error;
    }
  }

  // Get all relationships with optional filtering
  async getRelationships(filters: RelationshipFilters = {}): Promise<RelationshipResponse> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters.from_object_id) queryParams.append('from_object_id', filters.from_object_id.toString());
      if (filters.from_object_type) queryParams.append('from_object_type', filters.from_object_type);
      if (filters.to_object_id) queryParams.append('to_object_id', filters.to_object_id.toString());
      if (filters.to_object_type) queryParams.append('to_object_type', filters.to_object_type);
      if (filters.relationship_type) queryParams.append('relationship_type', filters.relationship_type);
      if (filters.limit) queryParams.append('limit', filters.limit.toString());
      if (filters.offset) queryParams.append('offset', filters.offset.toString());
      
      const queryString = queryParams.toString();
      const url = `/relationships${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching relationships:', error);
      throw error;
    }
  }

  // Get relationships from a specific object
  async getRelationshipsFromObject(objectId: string, objectType: string, limit: number = 50, offset: number = 0): Promise<RelationshipResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (limit) queryParams.append('limit', limit.toString());
      if (offset) queryParams.append('offset', offset.toString());
      
      const queryString = queryParams.toString();
      const url = `/relationships/from/${objectId}/${objectType}${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching relationships from object:', error);
      throw error;
    }
  }

  // Get relationships to a specific object
  async getRelationshipsToObject(objectId: string, objectType: string, limit: number = 50, offset: number = 0): Promise<RelationshipResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (limit) queryParams.append('limit', limit.toString());
      if (offset) queryParams.append('offset', offset.toString());
      
      const queryString = queryParams.toString();
      const url = `/relationships/to/${objectId}/${objectType}${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching relationships to object:', error);
      throw error;
    }
  }

  // Update a relationship
  async updateRelationship(relationshipId: number, updateData: UpdateRelationshipData): Promise<RelationshipResponse> {
    try {
      const response = await makeAuthenticatedRequest(`/relationships/${relationshipId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });
      
      return response;
    } catch (error) {
      console.error('Error updating relationship:', error);
      throw error;
    }
  }

  // Delete a relationship by ID
  async deleteRelationship(relationshipId: number): Promise<RelationshipResponse> {
    try {
      const response = await makeAuthenticatedRequest(`/relationships/${relationshipId}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error deleting relationship:', error);
      throw error;
    }
  }

  // Delete a relationship by object references
  async deleteRelationshipByObjects(
    fromObjectId: number, 
    fromObjectType: string, 
    toObjectId: number, 
    toObjectType: string
  ): Promise<RelationshipResponse> {
    try {
      const url = `/relationships/from/${fromObjectId}/${fromObjectType}/to/${toObjectId}/${toObjectType}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error deleting relationship by objects:', error);
      throw error;
    }
  }

  // Get relationship statistics
  async getRelationshipStats(): Promise<{ success: boolean; stats?: RelationshipStats; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest('/relationships/stats/overview', {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching relationship statistics:', error);
      throw error;
    }
  }

  // Helper method to create a relationship between two objects
  async createObjectRelationship(
    fromObjectId: string,
    fromObjectType: ObjectType,
    toObjectId: string,
    toObjectType: ObjectType,
    notes?: string,
    relationshipType?: string
  ): Promise<RelationshipResponse> {
    const relationshipData: CreateRelationshipData = {
      from_object_id: fromObjectId,
      from_object_type: fromObjectType,
      to_object_id: toObjectId,
      to_object_type: toObjectType,
      relationship_type: relationshipType,
      notes
    };

    return this.createRelationship(relationshipData);
  }

  // Helper method to get all relationships for a specific object (both directions)
  async getObjectRelationships(objectId: string, objectType: string, limit: number = 50): Promise<{
    fromRelationships: Relationship[];
    toRelationships: Relationship[];
    totalFrom: number;
    totalTo: number;
  }> {
    try {
      const [fromResponse, toResponse] = await Promise.all([
        this.getRelationshipsFromObject(objectId, objectType, limit, 0),
        this.getRelationshipsToObject(objectId, objectType, limit, 0)
      ]);

      return {
        fromRelationships: fromResponse.relationships || [],
        toRelationships: toResponse.relationships || [],
        totalFrom: fromResponse.pagination?.total || 0,
        totalTo: toResponse.pagination?.total || 0
      };
    } catch (error) {
      console.error('Error fetching object relationships:', error);
      throw error;
    }
  }

  // Helper method to check if a relationship exists between two objects
  async relationshipExists(
    fromObjectId: string,
    fromObjectType: string,
    toObjectId: string,
    toObjectType: string
  ): Promise<boolean> {
    try {
      const filters: RelationshipFilters = {
        from_object_id: fromObjectId,
        from_object_type: fromObjectType,
        to_object_id: toObjectId,
        to_object_type: toObjectType,
        limit: 1
      };

      const response = await this.getRelationships(filters);
      return !!(response.relationships && response.relationships.length > 0);
    } catch (error) {
      console.error('Error checking relationship existence:', error);
      return false;
    }
  }

  // Helper method to get related objects of a specific type
  async getRelatedObjects(
    objectId: string,
    objectType: string,
    relatedObjectType: string,
    direction: 'from' | 'to' | 'both' = 'both',
    limit: number = 50
  ): Promise<{
    fromObjects: Array<{ id: string; relationship: Relationship }>;
    toObjects: Array<{ id: string; relationship: Relationship }>;
  }> {
    try {
      const result = {
        fromObjects: [] as Array<{ id: string; relationship: Relationship }>,
        toObjects: [] as Array<{ id: string; relationship: Relationship }>
      };

      if (direction === 'from' || direction === 'both') {
        const fromResponse = await this.getRelationshipsFromObject(objectId, objectType, limit, 0);
        if (fromResponse.data && fromResponse.data.relationships) {
          result.fromObjects = fromResponse.data.relationships
            .filter((rel: Relationship) => rel.to_object_type === relatedObjectType)
            .map((rel: Relationship) => ({ id: rel.to_object_id, relationship: rel }));
        }
      }

      if (direction === 'to' || direction === 'both') {
        const toResponse = await this.getRelationshipsToObject(objectId, objectType, limit, 0);
        if (toResponse.data && toResponse.data.relationships) {
          result.toObjects = toResponse.data.relationships
            .filter((rel: Relationship) => rel.from_object_type === relatedObjectType)
            .map((rel: Relationship) => ({ id: rel.from_object_id, relationship: rel }));
        }
      }

      return result;
    } catch (error) {
      console.error('Error fetching related objects:', error);
      throw error;
    }
  }

  // Helper method to create bidirectional relationships
  async createBidirectionalRelationship(
    object1Id: string,
    object1Type: string,
    object2Id: string,
    object2Type: string,
    notes?: string
  ): Promise<{
    forwardRelationship: RelationshipResponse;
    backwardRelationship: RelationshipResponse;
  }> {
    try {
      const [forwardResponse, backwardResponse] = await Promise.all([
        this.createObjectRelationship(object1Id, object1Type as ObjectType, object2Id, object2Type as ObjectType, notes),
        this.createObjectRelationship(object2Id, object2Type as ObjectType, object1Id, object1Type as ObjectType, notes)
      ]);

      return {
        forwardRelationship: forwardResponse,
        backwardRelationship: backwardResponse
      };
    } catch (error) {
      console.error('Error creating bidirectional relationship:', error);
      throw error;
    }
  }

  // Helper method to get relationship network for an object
  async getRelationshipNetwork(
    objectId: string,
    objectType: string,
    maxDepth: number = 2,
    limit: number = 50
  ): Promise<{
    directRelationships: Relationship[];
    indirectRelationships: Relationship[];
    networkStats: {
      totalObjects: number;
      totalRelationships: number;
      averageConnections: number;
    };
  }> {
    try {
      // Get direct relationships
      const directResponse = await this.getObjectRelationships(objectId, objectType, limit);
      const directRelationships = [
        ...directResponse.fromRelationships,
        ...directResponse.toRelationships
      ];

      // For now, we'll just return direct relationships
      // In a more complex implementation, you could traverse the network
      const uniqueObjectIds = new Set<string>();
      directRelationships.forEach(rel => {
        if (rel.from_object_id !== objectId) uniqueObjectIds.add(rel.from_object_id);
        if (rel.to_object_id !== objectId) uniqueObjectIds.add(rel.to_object_id);
      });

      return {
        directRelationships,
        indirectRelationships: [], // Would be populated in a more complex implementation
        networkStats: {
          totalObjects: uniqueObjectIds.size,
          totalRelationships: directRelationships.length,
          averageConnections: directRelationships.length / Math.max(uniqueObjectIds.size, 1)
        }
      };
    } catch (error) {
      console.error('Error fetching relationship network:', error);
      throw error;
    }
  }
}

export const relationshipService = new RelationshipService();
